import * as React from 'react';
import ReactDOM from 'react-dom';
import { useRecoilValue } from 'recoil';
import { AFormFields } from '../../atoms/forms';
import { AProducts } from '../../atoms/product';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import { FilterResult, filterTreeLength } from '../../components_v2/filter/model/Model';
import { Checkbox } from '../../components_v2/filterList/style/Style';
import InputSearch from '../../components_v2/input/InputSearch';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import { Column, Table } from '../../components_v2/table/Table';
import Toolbar from '../../components_v2/toolbar/Toolbar';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import { DefaultButton, DefaultSecondaryButton } from '../../styles/global/css/GlobalButton';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { Translate, translateToString } from '../../styles/global/translate';
import { NoMarginH5 } from '../authorization/users/Members';
import CompaniesAndMapView from '../client-companies/CompaniesAndMapView';
import { CompanySelection } from '../client-companies/Companies';
import { getRawCompanies } from '../client-companies/data/action';
import { CompanyParams } from '../client-companies/model/Model';
import { ToolbarBox } from '../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../globals/mainPage/mainPage';
import { getBrandsData } from '../products/action';
import { FlexDiv } from '../products/style';
import { defaultFilters } from '../reports/interpretor/ReportInterpretor';
import { getProductFieldValues } from './actions';
import { getProductDimensionValues } from '../products/action';
import { collectProductDimFieldIds, matchProductTree, productFilterList } from './perimeterFilters';

export type PerimeterItem = { id: number, name: string };
type OnApply = (items: PerimeterItem[], replace: boolean) => void;

const PERIMETER_COMPANIES_LIMIT = 50_000;

// count is undefined when the whole filtered list applies and its size is
// only known server-side
function PerimeterFooter(props: { count?: number, onCancel: () => void, onApply: (replace: boolean) => void, disabled?: boolean }) {
	return <ToolbarBox>
		{props.count !== undefined && <DefaultTextDiv>{props.count}</DefaultTextDiv>}
		<DefaultSecondaryButton margin='0' onClick={props.onCancel}><Translate id='cancel' /></DefaultSecondaryButton>
		<DefaultButton margin='0' disabled={props.disabled || props.count === 0} onClick={() => props.onApply(false)}><Translate id='add' /></DefaultButton>
		<DefaultButton margin='0' disabled={props.disabled} onClick={() => props.onApply(true)}><Translate id='replace' /></DefaultButton>
	</ToolbarBox>;
}

// The TSP-like company picker: the full companies listing (filters, search,
// row checkboxes) in a popup. No checkbox = the whole filtered list; checked
// rows narrow to them; select-all keeps everything but the unchecked rows.
export function CompanyPerimeterPopup(props: {
	isOpen: boolean,
	setIsOpen: (isOpen: boolean) => void,
	onApply: OnApply,
}) {
	const [toolBar, setToolBarState] = React.useState<ToolbarState>();
	const [body, setBody] = React.useState<CompanyParams>();
	const [selection, setSelection] = React.useState<CompanySelection>({ isAllSelected: false, checked: [], unchecked: [] });
	const [applying, setApplying] = React.useState(false);

	const pinpointed = !selection.isAllSelected && selection.checked.length > 0;
	const apply = async(replace: boolean) => {
		setApplying(true);
		try {
			const query: CompanyParams = pinpointed
				? {
					columns: ['name'],
					limit: selection.checked.length,
					offset: 0,
					new_filters: { val: { column: 'company_id', value: selection.checked.toString(), operator: 'contained' } },
					order_bys: undefined,
				}
				: { ...body, columns: ['name'], limit: PERIMETER_COMPANIES_LIMIT, offset: 0, order_bys: undefined };
			const res = await getRawCompanies(query);
			let items: PerimeterItem[] = res.row.map(c => ({ id: c.companyId!, name: c.name! }));
			if (selection.isAllSelected && selection.unchecked.length > 0) {
				const excluded = new Set(selection.unchecked);
				items = items.filter(({ id }) => !excluded.has(id));
			}
			props.onApply(items, replace);
			props.setIsOpen(false);
		} catch (e) {
			console.error(e);
		}
		setApplying(false);
	};

	return <Popup
		isOpen={props.isOpen}
		popupMode={PopupMode.Centered}
		popupStyle={{ height: '90vh', width: '90vw', animate: false }}
		onClickOut={() => props.setIsOpen(false)}
	>
		<FlexDiv height='100%' width='100%' flow='column' align='stretch'>
			<Toolbar
				height='50px'
				leftComponent={<NoMarginH5><Translate id='companies' /></NoMarginH5>}
				rightComponent={<PerimeterFooter
					count={pinpointed ? selection.checked.length : undefined}
					onCancel={() => props.setIsOpen(false)}
					onApply={apply}
					disabled={applying}
				/>}
			/>
			<Toolbar leftComponent={<ToolbarBox>{toolBar?.bottomLeftToolbarComponent}</ToolbarBox>} />
			<CompaniesAndMapView
				setToolBarState={setToolBarState}
				listingOnly
				height='100%'
				setBody={setBody}
				onSelectionChange={setSelection}
			/>
		</FlexDiv>
	</Popup>;
}

// Checkbox state lives in a context so ticking one row re-renders the
// checkboxes alone: putting `checked` in the columns memo hands Table a new
// columns identity per tick, and it rebuilds every row — the popup flashes.
type CheckedContextType = {
	checked: Set<number>,
	toggle: (id: number) => void,
	setAll: (ids: number[]) => void,
};
const CheckedContext = React.createContext<CheckedContextType>({ checked: new Set(), toggle: () => undefined, setAll: () => undefined });

function RowCheck(props: { id: number }) {
	const { checked, toggle } = React.useContext(CheckedContext);
	return <Checkbox isActive={checked.has(props.id)} onClick={() => toggle(props.id)} />;
}

function HeaderCheck(props: { ids: number[] }) {
	const { checked, setAll } = React.useContext(CheckedContext);
	const allChecked = props.ids.length > 0 && props.ids.every(id => checked.has(id));
	return <Checkbox isActive={allChecked} onClick={() => setAll(allChecked ? [] : props.ids)} />;
}

// The product picker: search + product filters + checkbox list; same
// selection semantics as the company picker
export function ProductPerimeterPopup(props: {
	isOpen: boolean,
	setIsOpen: (isOpen: boolean) => void,
	onApply: OnApply,
}) {
	const products = useRecoilValue(AProducts);
	const allFields = useRecoilValue(AFormFields);
	const [brandNames, setBrandNames] = React.useState<Map<number, string>>(new Map());
	const [productFieldValues, setProductFieldValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	const [dimensionValues, setDimensionValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	const [filterOpen, setFilterOpen] = React.useState(false);
	const [filterResult, setFilterResult] = React.useState<FilterResult>(defaultFilters);
	// only the dimension fields the active filters reference are fetched
	React.useEffect(() => {
		const tree = filterResult && 'formatted' in filterResult ? filterResult.formatted : undefined;
		const missing = [...collectProductDimFieldIds(tree)].filter(id => !dimensionValues.has(id));
		if (missing.length === 0) return;
		getProductDimensionValues(missing)
			.then(fetched => setDimensionValues(prev => new Map([...prev, ...fetched])))
			.catch(console.error);
	}, [filterResult, dimensionValues]);
	const [search, setSearch] = React.useState('');
	const [checked, setChecked] = React.useState<Set<number>>(new Set());

	React.useEffect(() => {
		if (!props.isOpen) return;
		getBrandsData().then(brands => setBrandNames(new Map(brands.map(b => [b.id, b.name])))).catch(console.error);
		getProductFieldValues().then(rows => {
			const map = new Map<number, Map<string, unknown>>();
			rows.forEach(r => {
				if (!map.has(r.field_id)) map.set(r.field_id, new Map());
				map.get(r.field_id)?.set(r.product_uuid, r.value);
			});
			setProductFieldValues(map);
		}).catch(console.error);
	}, [props.isOpen]);

	const productFilters = React.useMemo(() => productFilterList(allFields, new Set(productFieldValues.keys())), [allFields, productFieldValues]);
	const filtered = React.useMemo(() => {
		const tree = filterResult?.formatted;
		const lowered = search.toLowerCase();
		return products.filter(p => (!lowered || p.name.toLowerCase().includes(lowered))
			&& (!tree || filterTreeLength(tree) === 0 || matchProductTree(p, tree, productFieldValues, dimensionValues)));
	}, [products, search, filterResult, productFieldValues, dimensionValues]);

	const toggle = React.useCallback((id: number) => setChecked(current => {
		const next = new Set(current);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		return next;
	}), []);
	const setAll = React.useCallback((ids: number[]) => setChecked(new Set(ids)), []);
	const checkedContext = React.useMemo(() => ({ checked, toggle, setAll }), [checked, toggle, setAll]);

	const columns = React.useMemo((): Column<typeof filtered[number]>[] => [
		{
			id: 'checkbox',
			noHeaderEllipsis: true,
			Header: <HeaderCheck ids={filtered.map(p => p.id)} />,
			accessor: row => <RowCheck id={row.id} />,
			width: 40,
			minWidth: 40,
			disableSortBy: true,
			unresizeable: true,
			centerHeader: true,
		},
		{
			id: 'name',
			Header: translateToString('name'),
			accessor: row => <DefaultTextDiv>{row.name}</DefaultTextDiv>,
			width: undefined,
			minWidth: 200,
			type: 'string',
		},
		{
			id: 'brand',
			Header: translateToString('brand'),
			accessor: row => <DefaultTextDiv>{row.brand !== undefined ? brandNames.get(row.brand) ?? '' : ''}</DefaultTextDiv>,
			width: 150,
			type: 'string',
		},
		{
			id: 'price',
			Header: translateToString('price'),
			accessor: row => <DefaultTextDiv>{row.price ?? ''}</DefaultTextDiv>,
			width: 100,
			type: 'string',
		},
	], [filtered, brandNames]);

	const selected = checked.size > 0 ? filtered.filter(p => checked.has(p.id)) : filtered;
	const apply = (replace: boolean) => {
		props.onApply(selected.map(({ id, name }) => ({ id, name })), replace);
		props.setIsOpen(false);
	};

	return <Popup
		isOpen={props.isOpen}
		popupMode={PopupMode.Centered}
		popupStyle={{ height: '90vh', width: '70vw', animate: false }}
		onClickOut={() => props.setIsOpen(false)}
	>
		<FlexDiv height='100%' width='100%' flow='column' align='stretch'>
			<Toolbar
				height='50px'
				leftComponent={<NoMarginH5><Translate id='general.products' /></NoMarginH5>}
				rightComponent={<PerimeterFooter
					count={selected.length}
					onCancel={() => props.setIsOpen(false)}
					onApply={apply}
				/>}
			/>
			<Toolbar
				leftComponent={<ToolbarBox>
					<InputSearch name='perimeter_product_search' type='text' value={search} onChange={setSearch} />
					<ToolbarFilterButton
						activeFilters={filterTreeLength(filterResult?.formatted)}
						onClick={() => setFilterOpen(true)}
						onDeleteFilter={() => setFilterResult(defaultFilters)}
					/>
				</ToolbarBox>}
			/>
			<CheckedContext.Provider value={checkedContext}>
				<Table
					height='calc(100% - 100px)'
					columns={columns}
					data={filtered}
				/>
			</CheckedContext.Provider>
			{/* portaled to the body: the centered popup is a transformed
			ancestor, which would anchor the panel's fixed position to the
			popup instead of the viewport and render it off screen */}
			{ReactDOM.createPortal(<AdvancedFilters
				isOpen={filterOpen}
				setOpen={setFilterOpen}
				filterList={[{ category: translateToString('general.products'), filters: productFilters }]}
				onChange={setFilterResult}
				filterValues={filterResult?.values}
			/>, document.body)}
		</FlexDiv>
	</Popup>;
}
